import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { restaurantApi, sectionsApi, billingApi, staffApi, authApi , onboardingApi } from '../services/api';
import { isNativeApp } from '../lib/platform';
import {
  Building2,
  Clock,
  Bell,
  CreditCard,
  Printer,
  Users,
  Shield,
  Save,
  Loader2,
  CheckCircle,
  LayoutGrid,
  Plus,
  Trash2,
  Edit2,
  ExternalLink,
  AlertCircle,
  Coffee,
  ChevronDown,
  ChevronUp,
  User,
  Mail,
  Lock,
  Scale,
  SlidersHorizontal,
  Plug,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';
import FloorPlanEditor from '../components/FloorPlanEditor';
import IntegrationsSettings from '../components/IntegrationsSettings';

type SettingsTab = 'account' | 'restaurant' | 'modules' | 'integrations' | 'floorplan' | 'hours' | 'notifications' | 'payments' | 'devices' | 'roles' | 'security';

const tabs: Array<{ id: SettingsTab; label: string; icon: typeof Building2 }> = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'restaurant', label: 'Restaurant', icon: Building2 },
  { id: 'modules', label: 'Modules', icon: SlidersHorizontal },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'floorplan', label: 'Floor Plan', icon: LayoutGrid },
  { id: 'hours', label: 'Hours', icon: Clock },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'devices', label: 'Devices', icon: Printer },
  { id: 'roles', label: 'Roles', icon: Users },
  { id: 'security', label: 'Security', icon: Shield },
];

// The features that can be turned on/off per account. Keys match the nav tags
// and MODULE_DEFAULTS in Layout.tsx. `def` is the default when unset.
const MODULE_OPTIONS: Array<{ key: string; label: string; desc: string; def: boolean }> = [
  { key: 'sales', label: 'Sales', desc: 'POS sales dashboard', def: true },
  { key: 'menu', label: 'Menu', desc: 'Menu items, categories, prices', def: true },
  { key: 'recipes', label: 'Recipes', desc: 'Recipe costing & bills of materials', def: true },
  { key: 'inventory', label: 'Inventory', desc: 'Stock counts & tracking', def: true },
  { key: 'purchasing', label: 'Purchasing', desc: 'Ordering, receiving, invoices, food cost', def: true },
  { key: 'production', label: 'Production', desc: 'For food makers — “what to make” from orders', def: false },
  { key: 'wholesale', label: 'Wholesale', desc: 'Wholesale customers, orders & invoices', def: false },
  { key: 'frontOfHouse', label: 'Front of House', desc: 'Host, reservations, guests', def: true },
  { key: 'team', label: 'Team', desc: 'Staff, shifts, labor, payroll', def: true },
  { key: 'events', label: 'Events', desc: 'Private events — inquiries, proposals, BEOs & deposits', def: true },
];

const SECTION_COLORS = [
  '#6366f1', // indigo
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
];

interface FloorSection {
  id: string;
  name: string;
  color: string;
  tables: FloorTable[];
}

interface FloorTable {
  id: string;
  number: string;
  name: string | null;
  capacity: number;
  shape: string;
  positionX: number;
  positionY: number;
  status: string;
}

interface Settings {
  restaurantName: string;
  address: string;
  phone: string;
  email: string;
  timezone: string;
  currency: string;
  taxRate: number;
  laborRatePerHour: number;
  defaultTipOptions: number[];
  autoClockOut: boolean;
  autoClockOutTime: string;
  emailNotifications: boolean;
  smsNotifications: boolean;
  lowStockAlerts: boolean;
  reservationAlerts: boolean;
  operatingHours: Record<string, { open: string; close: string; isOpen: boolean }>;
}

const defaultSettings: Settings = {
  restaurantName: '',
  address: '',
  phone: '',
  email: '',
  timezone: 'America/New_York',
  currency: 'USD',
  taxRate: 8.5,
  laborRatePerHour: 0,
  defaultTipOptions: [15, 18, 20, 25],
  autoClockOut: true,
  autoClockOutTime: '02:00',
  emailNotifications: true,
  smsNotifications: false,
  lowStockAlerts: true,
  reservationAlerts: true,
  operatingHours: {
    Monday: { open: '11:00', close: '22:00', isOpen: true },
    Tuesday: { open: '11:00', close: '22:00', isOpen: true },
    Wednesday: { open: '11:00', close: '22:00', isOpen: true },
    Thursday: { open: '11:00', close: '22:00', isOpen: true },
    Friday: { open: '11:00', close: '23:00', isOpen: true },
    Saturday: { open: '10:00', close: '23:00', isOpen: true },
    Sunday: { open: '10:00', close: '21:00', isOpen: true },
  },
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  // Deep-linkable tab (e.g. /settings?tab=payments from the setup checklist)
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return tabs.some((t) => t.id === requested) ? (requested as SettingsTab) : 'account';
  });
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [intBanner, setIntBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Account state
  const [accountForm, setAccountForm] = useState({ firstName: '', lastName: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [accountSaveSuccess, setAccountSaveSuccess] = useState(false);
  const [passwordSaveSuccess, setPasswordSaveSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Fetch current user data
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await authApi.me();
      return response.data?.data;
    },
  });

  // Load user data into account form
  useEffect(() => {
    if (userData?.user) {
      setAccountForm({
        firstName: userData.user.firstName || '',
        lastName: userData.user.lastName || '',
        email: userData.user.email || '',
      });
    }
  }, [userData]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string; email?: string }) =>
      authApi.updateProfile(data),
    onSuccess: (_res, vars) => {
      // The header shows the login-time snapshot — keep it current.
      try {
        const u = JSON.parse(localStorage.getItem('manager_user') || 'null');
        if (u) {
          if (vars.firstName !== undefined) u.firstName = vars.firstName;
          if (vars.lastName !== undefined) u.lastName = vars.lastName;
          if (vars.email !== undefined) u.email = vars.email;
          localStorage.setItem('manager_user', JSON.stringify(u));
        }
      } catch { /* cache refresh only */ }
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setAccountSaveSuccess(true);
      setTimeout(() => setAccountSaveSuccess(false), 3000);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSaveSuccess(true);
      setPasswordError('');
      setTimeout(() => setPasswordSaveSuccess(false), 3000);
    },
    onError: (error: any) => {
      setPasswordError(error.response?.data?.error?.message || 'Failed to change password');
    },
  });

  // Fetch restaurant data
  const { data: restaurantData, isLoading } = useQuery({
    queryKey: ['restaurant-current'],
    queryFn: async () => {
      const response = await restaurantApi.getCurrent();
      return response.data?.data || response.data;
    },
  });

  // Save settings mutation
  const saveMutation = useMutation({
    mutationFn: async (newSettings: Partial<Settings>) => {
      // Name/phone/email/timezone live on the restaurant RECORD, not the
      // settings JSON — saving them into settings only looked like it worked
      // while every other screen kept reading the untouched record.
      const { restaurantName, phone, email, timezone, ...rest } = newSettings as any;
      const record: Record<string, any> = {};
      if (restaurantName) record.name = restaurantName;
      if (phone !== undefined) record.phone = phone;
      if (email !== undefined && email !== '') record.email = email;
      if (timezone !== undefined && timezone !== '') record.timezone = timezone;
      if (Object.keys(record).length) await onboardingApi.updateRestaurant(record);
      await restaurantApi.updateSettings(rest);
      return record;
    },
    onSuccess: (record: any) => {
      // Keep the cached snapshot (header, switcher) in step immediately.
      if (record?.name) {
        try {
          const cur = JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null');
          if (cur) { cur.name = record.name; localStorage.setItem('manager_current_restaurant', JSON.stringify(cur)); }
        } catch { /* cache refresh only */ }
      }
      queryClient.invalidateQueries({ queryKey: ['restaurant-current'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant-modules'] }); // refresh nav visibility live
      queryClient.invalidateQueries({ queryKey: ['restaurant-forecast'] });
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  // Floor plan state
  const [editingSection, setEditingSection] = useState<FloorSection | null>(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  // Role permissions state
  const [expandedRole, setExpandedRole] = useState<string | null>(null);

  // Unit preferences state
  const [unitPreferencesSaveSuccess, setUnitPreferencesSaveSuccess] = useState(false);

  // Fetch floor plan
  const { data: sectionsData, isLoading: sectionsLoading, refetch: refetchSections } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return response.data?.data || [];
    },
    enabled: activeTab === 'floorplan',
  });

  // Section mutations
  const createSectionMutation = useMutation({
    mutationFn: (data: { name: string; color?: string }) => sectionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      setShowAddSection(false);
      setNewSectionName('');
    },
  });

  const updateSectionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string } }) =>
      sectionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      setEditingSection(null);
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (id: string) => sectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });

  // Fetch role permissions
  const { data: rolePermissionsData, isLoading: rolePermissionsLoading } = useQuery({
    queryKey: ['role-permissions'],
    queryFn: async () => {
      const response = await staffApi.getRolePermissions();
      return response.data?.data || [];
    },
    enabled: activeTab === 'roles',
  });

  // Update role permissions mutation
  const updateRolePermissionsMutation = useMutation({
    mutationFn: ({ role, data }: { role: string; data: any }) =>
      staffApi.updateRolePermissions(role, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
    },
  });

  // Fetch unit preferences
  const { data: unitPreferencesData, isLoading: unitPreferencesLoading } = useQuery({
    queryKey: ['unit-preferences'],
    queryFn: async () => {
      const response = await restaurantApi.getUnitPreferences();
      return response.data?.data || { preferences: {}, options: {} };
    },
  });

  // Update unit preferences mutation
  const updateUnitPreferencesMutation = useMutation({
    mutationFn: (preferences: { weight?: string; volume?: string; count?: string }) =>
      restaurantApi.updateUnitPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit-preferences'] });
      setUnitPreferencesSaveSuccess(true);
      setTimeout(() => setUnitPreferencesSaveSuccess(false), 3000);
    },
  });

  // Load restaurant data into settings
  useEffect(() => {
    if (restaurantData) {
      const apiSettings = restaurantData.settings || {};
      setSettings({
        ...defaultSettings,
        restaurantName: restaurantData.name || '',
        address: restaurantData.address || '',
        phone: restaurantData.phone || '',
        email: restaurantData.email || '',
        timezone: restaurantData.timezone || apiSettings.timezone || defaultSettings.timezone,
        currency: apiSettings.currency || defaultSettings.currency,
        taxRate: apiSettings.taxRate ?? defaultSettings.taxRate,
        laborRatePerHour: (restaurantData.laborRateCentsPerHour ?? 0) / 100,
        defaultTipOptions: apiSettings.defaultTipOptions || defaultSettings.defaultTipOptions,
        autoClockOut: apiSettings.autoClockOut ?? defaultSettings.autoClockOut,
        autoClockOutTime: apiSettings.autoClockOutTime || defaultSettings.autoClockOutTime,
        emailNotifications: apiSettings.emailNotifications ?? defaultSettings.emailNotifications,
        smsNotifications: apiSettings.smsNotifications ?? defaultSettings.smsNotifications,
        lowStockAlerts: apiSettings.lowStockAlerts ?? defaultSettings.lowStockAlerts,
        reservationAlerts: apiSettings.reservationAlerts ?? defaultSettings.reservationAlerts,
        operatingHours: apiSettings.operatingHours || defaultSettings.operatingHours,
      });
    }
  }, [restaurantData]);

  const updateSetting = (key: keyof Settings, value: unknown) => {
    setSettings({ ...settings, [key]: value });
    setHasChanges(true);
  };

  const updateHours = (day: string, field: 'open' | 'close' | 'isOpen', value: string | boolean) => {
    const newHours = {
      ...settings.operatingHours,
      [day]: {
        ...settings.operatingHours[day],
        [field]: value,
      },
    };
    setSettings({ ...settings, operatingHours: newHours });
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(settings);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
            <p className="text-slate-500">Configure your restaurant settings</p>
          </div>
          <HelpButton featureId="settings" />
        </div>
        <div className="flex items-center gap-3">
          {saveSuccess && (
            <span className="flex items-center gap-2 text-success-600">
              <CheckCircle className="w-4 h-4" />
              Saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className={clsx(
              'btn flex items-center gap-2',
              hasChanges ? 'btn-primary' : 'btn-secondary opacity-50'
            )}
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Mobile horizontal tabs */}
      <div className="md:hidden mb-4 -mx-4 px-4 overflow-x-auto">
        <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors',
                activeTab === id
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-slate-600 bg-slate-100 hover:bg-slate-200'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Desktop vertical tabs */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <nav className="space-y-1">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                  activeTab === id
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-slate-600 hover:bg-slate-100'
                )}
              >
                <Icon className="w-5 h-5" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'integrations' && (
            <div className="space-y-3">
              {intBanner && (
                <div className={clsx('rounded-xl px-4 py-2.5 text-sm', intBanner.tone === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800')}>
                  {intBanner.text}
                </div>
              )}
              <IntegrationsSettings say={(tone, text) => setIntBanner({ tone, text })} />
            </div>
          )}
          {activeTab === 'account' && (
            <div className="space-y-6">
              {/* Profile Information */}
              <div className="card p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Profile Information</h2>
                    <p className="text-sm text-slate-500">Update your account details</p>
                  </div>
                  {accountSaveSuccess && (
                    <span className="flex items-center gap-2 text-success-600">
                      <CheckCircle className="w-4 h-4" />
                      Saved
                    </span>
                  )}
                </div>

                {userLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={accountForm.firstName}
                        onChange={(e) => setAccountForm({ ...accountForm, firstName: e.target.value })}
                        className="input w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={accountForm.lastName}
                        onChange={(e) => setAccountForm({ ...accountForm, lastName: e.target.value })}
                        className="input w-full"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={accountForm.email}
                        onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                        className="input w-full"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        This is the email you use to sign in
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        onClick={() => updateProfileMutation.mutate(accountForm)}
                        disabled={updateProfileMutation.isPending}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        {updateProfileMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        Save Profile
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Change Password */}
              <div className="card p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Lock className="w-5 h-5" />
                      Change Password
                    </h2>
                    <p className="text-sm text-slate-500">Update your password</p>
                  </div>
                  {passwordSaveSuccess && (
                    <span className="flex items-center gap-2 text-success-600">
                      <CheckCircle className="w-4 h-4" />
                      Password changed
                    </span>
                  )}
                </div>

                {passwordError && (
                  <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-2 text-danger-700">
                    <AlertCircle className="w-4 h-4" />
                    {passwordError}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <button
                      onClick={() => {
                        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                          setPasswordError('Passwords do not match');
                          return;
                        }
                        if (passwordForm.newPassword.length < 8) {
                          setPasswordError('Password must be at least 8 characters');
                          return;
                        }
                        setPasswordError('');
                        changePasswordMutation.mutate({
                          currentPassword: passwordForm.currentPassword,
                          newPassword: passwordForm.newPassword,
                        });
                      }}
                      disabled={changePasswordMutation.isPending || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      {changePasswordMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'modules' && (
            <div className="card p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Modules</h2>
                <p className="text-sm text-slate-500">Turn features on or off for this account. Changes take effect the next time the app loads.</p>
              </div>
              {(() => {
                const current = (restaurantData?.settings?.modules || {}) as Record<string, boolean>;
                const isOn = (o: typeof MODULE_OPTIONS[number]) => (typeof current[o.key] === 'boolean' ? current[o.key] : o.def);
                const toggle = (key: string, value: boolean) => {
                  const full: Record<string, boolean> = {};
                  for (const o of MODULE_OPTIONS) full[o.key] = typeof current[o.key] === 'boolean' ? current[o.key] : o.def;
                  full[key] = value;
                  saveMutation.mutate({ modules: full } as any);
                };
                return (
                  <div className="divide-y divide-slate-100">
                    {MODULE_OPTIONS.map((o) => {
                      const on = isOn(o);
                      return (
                        <div key={o.key} className="flex items-center justify-between gap-4 py-3">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">{o.label}</p>
                            <p className="text-sm text-slate-500">{o.desc}</p>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={on}
                            disabled={saveMutation.isPending}
                            onClick={() => toggle(o.key, !on)}
                            className={clsx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors', on ? 'bg-primary-600' : 'bg-slate-300')}
                          >
                            <span className={clsx('inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform', on ? 'translate-x-5' : 'translate-x-0.5')} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <p className="text-xs text-slate-400">Dashboard, Reports and Settings are always available.</p>
            </div>
          )}

          {activeTab === 'restaurant' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Restaurant Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Restaurant Name
                  </label>
                  <input
                    type="text"
                    value={settings.restaurantName}
                    onChange={(e) => updateSetting('restaurantName', e.target.value)}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={settings.phone}
                    onChange={(e) => updateSetting('phone', e.target.value)}
                    className="input w-full"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={settings.address}
                    onChange={(e) => updateSetting('address', e.target.value)}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={settings.email}
                    onChange={(e) => updateSetting('email', e.target.value)}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => updateSetting('timezone', e.target.value)}
                    className="input w-full"
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Currency
                  </label>
                  <select
                    value={settings.currency}
                    onChange={(e) => updateSetting('currency', e.target.value)}
                    className="input w-full"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (&euro;)</option>
                    <option value="GBP">GBP (&pound;)</option>
                    <option value="CAD">CAD ($)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={settings.taxRate}
                    onChange={(e) => updateSetting('taxRate', parseFloat(e.target.value) || 0)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Kitchen Labour Rate ($/hour)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={settings.laborRatePerHour}
                    onChange={(e) => updateSetting('laborRatePerHour', parseFloat(e.target.value) || 0)}
                    className="input w-full"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Used to cost recipe labour minutes into each dish's food cost.
                  </p>
                </div>
              </div>

              {/* Unit Preferences Section */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Scale className="w-5 h-5 text-slate-600" />
                    <div>
                      <h3 className="font-semibold">Unit Preferences</h3>
                      <p className="text-sm text-slate-500">Choose your preferred display units for inventory and recipes</p>
                    </div>
                  </div>
                  {unitPreferencesSaveSuccess && (
                    <span className="flex items-center gap-2 text-success-600 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Saved
                    </span>
                  )}
                </div>

                {unitPreferencesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Weight Units
                      </label>
                      <select
                        value={unitPreferencesData?.preferences?.weight || 'lb'}
                        onChange={(e) => updateUnitPreferencesMutation.mutate({ weight: e.target.value })}
                        disabled={updateUnitPreferencesMutation.isPending}
                        className="input w-full"
                      >
                        {(unitPreferencesData?.options?.weight || []).map((opt: { value: string; label: string }) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Volume Units
                      </label>
                      <select
                        value={unitPreferencesData?.preferences?.volume || 'cup'}
                        onChange={(e) => updateUnitPreferencesMutation.mutate({ volume: e.target.value })}
                        disabled={updateUnitPreferencesMutation.isPending}
                        className="input w-full"
                      >
                        {(unitPreferencesData?.options?.volume || []).map((opt: { value: string; label: string }) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Count Units
                      </label>
                      <select
                        value={unitPreferencesData?.preferences?.count || 'each'}
                        onChange={(e) => updateUnitPreferencesMutation.mutate({ count: e.target.value })}
                        disabled={updateUnitPreferencesMutation.isPending}
                        className="input w-full"
                      >
                        {(unitPreferencesData?.options?.count || []).map((opt: { value: string; label: string }) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <p className="text-xs text-slate-500 mt-3">
                  These settings control how quantities are displayed throughout the app. Inventory and recipe quantities will automatically convert to your preferred units.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'floorplan' && (
            <div className="space-y-6">
              {/* Section Management */}
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Sections</h2>
                  <button
                    onClick={() => setShowAddSection(true)}
                    className="btn btn-primary btn-sm flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Add Section
                  </button>
                </div>

                {sectionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  </div>
                ) : !sectionsData?.length ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <LayoutGrid className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                    <p className="text-slate-600 font-medium">No sections yet</p>
                    <p className="text-slate-500 text-sm mb-3">Create sections to organize your tables</p>
                    <button
                      onClick={() => setShowAddSection(true)}
                      className="btn btn-primary btn-sm"
                    >
                      Create First Section
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {sectionsData.map((section: FloorSection) => (
                      <div
                        key={section.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: section.color }}
                        />
                        {editingSection?.id === section.id ? (
                          <>
                            <input
                              type="text"
                              value={editingSection.name}
                              onChange={(e) => setEditingSection({ ...editingSection, name: e.target.value })}
                              className="input py-0.5 px-2 text-sm w-28"
                              autoFocus
                            />
                            <button
                              onClick={() => updateSectionMutation.mutate({
                                id: section.id,
                                data: { name: editingSection.name, color: editingSection.color }
                              })}
                              className="text-xs text-primary-600 hover:text-primary-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingSection(null)}
                              className="text-xs text-slate-500 hover:text-slate-700"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="font-medium text-sm">{section.name}</span>
                            <span className="text-xs text-slate-500">({section.tables?.length || 0})</span>
                            <button
                              onClick={() => setEditingSection(section)}
                              className="p-1 text-slate-400 hover:text-slate-600"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`Delete section "${section.name}" and all its tables?`)) {
                                  deleteSectionMutation.mutate(section.id);
                                }
                              }}
                              className="p-1 text-slate-400 hover:text-danger-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Visual Floor Plan Editor */}
              {sectionsData?.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-lg font-semibold mb-4">Table Layout</h2>
                  <p className="text-slate-500 text-sm mb-4">
                    Drag tables to position them. Click on the canvas to add new tables.
                  </p>
                  <FloorPlanEditor
                    sections={sectionsData}
                    onUpdate={() => refetchSections()}
                  />
                </div>
              )}

              {/* Add Section Modal */}
              {showAddSection && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                    <h3 className="text-lg font-semibold mb-4">Add New Section</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Section Name
                        </label>
                        <input
                          type="text"
                          value={newSectionName}
                          onChange={(e) => setNewSectionName(e.target.value)}
                          className="input w-full"
                          placeholder="e.g., Main Dining, Patio, Bar"
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => {
                            setShowAddSection(false);
                            setNewSectionName('');
                          }}
                          className="btn btn-ghost"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            if (newSectionName.trim()) {
                              createSectionMutation.mutate({
                                name: newSectionName.trim(),
                                color: SECTION_COLORS[(sectionsData?.length || 0) % SECTION_COLORS.length]
                              });
                            }
                          }}
                          disabled={!newSectionName.trim() || createSectionMutation.isPending}
                          className="btn btn-primary"
                        >
                          {createSectionMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Create Section'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Notification Preferences</h2>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                  <div>
                    <span className="font-medium">Email Notifications</span>
                    <p className="text-sm text-slate-500">
                      Receive daily reports and alerts via email
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                    className="w-5 h-5 accent-primary-600"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                  <div>
                    <span className="font-medium">SMS Notifications</span>
                    <p className="text-sm text-slate-500">
                      Receive urgent alerts via text message
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.smsNotifications}
                    onChange={(e) => updateSetting('smsNotifications', e.target.checked)}
                    className="w-5 h-5 accent-primary-600"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                  <div>
                    <span className="font-medium">Low Stock Alerts</span>
                    <p className="text-sm text-slate-500">
                      Get notified when inventory items are running low
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.lowStockAlerts}
                    onChange={(e) => updateSetting('lowStockAlerts', e.target.checked)}
                    className="w-5 h-5 accent-primary-600"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                  <div>
                    <span className="font-medium">Reservation Alerts</span>
                    <p className="text-sm text-slate-500">
                      Notifications for new and updated reservations
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.reservationAlerts}
                    onChange={(e) => updateSetting('reservationAlerts', e.target.checked)}
                    className="w-5 h-5 accent-primary-600"
                  />
                </label>

                <DailyReportCard />
              </div>
            </div>
          )}

          {activeTab === 'hours' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Operating Hours</h2>
              <p className="text-slate-500">Configure your restaurant's operating hours</p>

              <div className="space-y-4">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                  const hours = settings.operatingHours[day] || { open: '11:00', close: '22:00', isOpen: true };
                  return (
                    <div key={day} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                      <span className="w-32 font-medium">{day}</span>
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) => updateHours(day, 'open', e.target.value)}
                        className="input w-auto"
                        disabled={!hours.isOpen}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) => updateHours(day, 'close', e.target.value)}
                        className="input w-auto"
                        disabled={!hours.isOpen}
                      />
                      <label className="flex items-center gap-2 ml-auto">
                        <input
                          type="checkbox"
                          checked={hours.isOpen}
                          onChange={(e) => updateHours(day, 'isOpen', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Open</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Payment Settings</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Default Tip Options (%)
                  </label>
                  <div className="flex gap-2">
                    {settings.defaultTipOptions.map((tip, index) => (
                      <input
                        key={index}
                        type="number"
                        value={tip}
                        onChange={(e) => {
                          const newTips = [...settings.defaultTipOptions];
                          newTips[index] = parseInt(e.target.value) || 0;
                          updateSetting('defaultTipOptions', newTips);
                        }}
                        className="input w-20"
                      />
                    ))}
                  </div>
                </div>

                {/* Receive card payments into the restaurant's own bank
                    (Stripe Connect). Shown on native too: this is payout
                    setup for physical-goods sales, not in-app purchase —
                    the onboarding link opens in the system browser. Only
                    OUR subscription billing below stays web-only (3.1.1). */}
                <ConnectPayoutsCard />

                {/* Payment/billing management is web-only (App Store Guideline 3.1.1). */}
                {!isNativeApp && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium mb-2">Mise subscription</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Your Mise plan, invoices and payment method — separate
                      from "Receive Payments" above, which is where guest card
                      payments reach your bank.
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const response = await billingApi.createPortalSession(window.location.href);
                          const url = response.data?.data?.url;
                          if (url) {
                            window.open(url, '_blank');
                          } else {
                            alert('Stripe is not configured. Please contact support to set up payments.');
                          }
                        } catch (error: any) {
                          if (error.response?.status === 404 || error.response?.status === 500) {
                            alert('Stripe is not configured yet. Please contact support to enable payment processing.');
                          } else {
                            alert('Failed to open Stripe portal. Please try again.');
                          }
                        }
                      }}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Manage Stripe
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'devices' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Device Settings</h2>

              {/* Printers — configured once here, every POS device inherits it */}
              <PrinterConfigCard />

              {/* Restaurant Code for POS/KDS Login */}
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <h3 className="font-medium text-primary-900 mb-2">Restaurant Login Code</h3>
                <p className="text-sm text-primary-700 mb-3">
                  Staff use this code to connect POS and KDS devices to your restaurant:
                </p>
                <div className="flex items-center gap-3">
                  <code className="bg-white px-4 py-2 rounded-lg border border-primary-200 font-mono text-lg font-bold text-primary-800">
                    {restaurantData?.slug || 'loading...'}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(restaurantData?.slug || '');
                      alert('Restaurant code copied!');
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-xs text-primary-600 mt-2">
                  Or use direct links: pos.lamise.app/?r={restaurantData?.slug} or kds.lamise.app/?r={restaurantData?.slug}
                </p>
              </div>

              <div className="space-y-4">
                {/* Receipt Printer Setup */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Printer className="w-5 h-5 text-slate-600" />
                    Receipt Printer Setup
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    The POS supports multiple printer connection methods:
                  </p>

                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-medium text-sm text-slate-800 mb-1">1. Browser Print (Default)</h4>
                      <p className="text-xs text-slate-600">
                        Works on any device. When you tap Print Receipt, a print dialog opens. Select your printer from the system dialog.
                        Works with any USB, Bluetooth, or network printer connected to your device.
                      </p>
                    </div>

                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-medium text-sm text-slate-800 mb-1">2. USB Thermal Printer (Chrome on Desktop)</h4>
                      <p className="text-xs text-slate-600 mb-2">
                        Direct connection to ESC/POS thermal printers via USB. Supported brands:
                      </p>
                      <ul className="text-xs text-slate-600 list-disc list-inside mb-2">
                        <li>Epson TM series (TM-T20, TM-T88, etc.)</li>
                        <li>Star Micronics (TSP100, TSP600, etc.)</li>
                        <li>Bixolon SRP series</li>
                      </ul>
                      <p className="text-xs text-slate-600">
                        <strong>Setup:</strong> Connect printer via USB. Chrome will prompt you to select the device when printing.
                      </p>
                    </div>

                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-medium text-sm text-slate-800 mb-1">3. Network Printer (iPad/Tablet)</h4>
                      <p className="text-xs text-slate-600 mb-2">
                        For wireless receipt printing on tablets. Supported systems:
                      </p>
                      <ul className="text-xs text-slate-600 list-disc list-inside mb-2">
                        <li>Star CloudPRNT enabled printers</li>
                        <li>Epson ePOS-enabled printers</li>
                        <li>Any printer with HTTP print API</li>
                      </ul>
                      <p className="text-xs text-slate-600">
                        <strong>Setup:</strong> Connect printer to WiFi network. Note the printer's IP address.
                        The POS will detect the printer automatically if it's on the same network.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      <strong>Tip:</strong> For the best experience on iPad, use a Star Micronics or Epson printer with built-in
                      WiFi/CloudPRNT support. For desktop POS stations, direct USB connection provides the fastest printing.
                    </p>
                  </div>
                </div>

                {/* Kitchen Display System */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-2">Kitchen Display System (KDS)</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Mount a tablet in your kitchen to display incoming orders. Staff sign in with their PIN.
                  </p>
                  <div className="flex items-center gap-3">
                    <a
                      href={`https://kds.lamise.app/?r=${restaurantData?.slug || ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open KDS App
                    </a>
                    <a
                      href={`https://pos.lamise.app/?r=${restaurantData?.slug || ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open POS App
                    </a>
                  </div>
                </div>

                {/* Kitchen Ticket Printer */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-2">Kitchen Ticket Printer (Optional)</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    If you prefer printed kitchen tickets instead of or alongside the KDS:
                  </p>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600">•</span>
                      <span>Use the same thermal printer setup as receipt printing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600">•</span>
                      <span>Kitchen tickets print automatically when orders are submitted</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600">•</span>
                      <span>Route items to specific stations (Grill, Fry, Cold, etc.) based on menu item settings</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Staff Roles & Permissions</h2>
              <p className="text-slate-500 text-sm">
                Configure shift workflow settings for each role. Click on a role to expand and edit its permissions.
              </p>

              {rolePermissionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {(rolePermissionsData || []).map((rolePerms: any) => {
                    const isExpanded = expandedRole === rolePerms.role;
                    const roleLabels: Record<string, string> = {
                      server: 'Server',
                      bartender: 'Bartender',
                      host: 'Host',
                      cook: 'Cook',
                      manager: 'Manager',
                      busser: 'Busser',
                      dishwasher: 'Dishwasher',
                    };
                    const roleDescriptions: Record<string, string> = {
                      server: 'Takes orders, serves tables, processes payments',
                      bartender: 'Serves bar, makes drinks, handles bar orders',
                      host: 'Manages reservations, seats guests, handles waitlist',
                      cook: 'Prepares food, views KDS orders',
                      manager: 'Full access to dashboard and settings',
                      busser: 'Clears tables, assists servers',
                      dishwasher: 'Kitchen support staff',
                    };

                    return (
                      <div key={rolePerms.role} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedRole(isExpanded ? null : rolePerms.role)}
                          className="w-full p-4 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Users className="w-5 h-5 text-slate-500" />
                            <div className="text-left">
                              <h3 className="font-medium">{roleLabels[rolePerms.role] || rolePerms.role}</h3>
                              <p className="text-xs text-slate-500">{roleDescriptions[rolePerms.role]}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {updateRolePermissionsMutation.isPending &&
                             updateRolePermissionsMutation.variables?.role === rolePerms.role && (
                              <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                            )}
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="p-4 space-y-4 bg-white border-t border-slate-200">
                            {/* Clock-in Settings */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Clock-in Settings
                              </h4>

                              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
                                <div>
                                  <span className="text-sm font-medium">Can clock in without approval</span>
                                  <p className="text-xs text-slate-500">Allow self-service clock in</p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={rolePerms.canClockInWithoutApproval}
                                  onChange={(e) => updateRolePermissionsMutation.mutate({
                                    role: rolePerms.role,
                                    data: { canClockInWithoutApproval: e.target.checked }
                                  })}
                                  className="w-5 h-5 accent-primary-600"
                                />
                              </label>

                              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
                                <div>
                                  <span className="text-sm font-medium">Requires section assignment</span>
                                  <p className="text-xs text-slate-500">Must select section during pre-shift</p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={rolePerms.requiresSectionAssignment}
                                  onChange={(e) => updateRolePermissionsMutation.mutate({
                                    role: rolePerms.role,
                                    data: { requiresSectionAssignment: e.target.checked }
                                  })}
                                  className="w-5 h-5 accent-primary-600"
                                />
                              </label>

                              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
                                <div>
                                  <span className="text-sm font-medium">Requires pre-shift checklist</span>
                                  <p className="text-xs text-slate-500">Must complete checklist before starting</p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={rolePerms.requiresPreShiftChecklist}
                                  onChange={(e) => updateRolePermissionsMutation.mutate({
                                    role: rolePerms.role,
                                    data: { requiresPreShiftChecklist: e.target.checked }
                                  })}
                                  className="w-5 h-5 accent-primary-600"
                                />
                              </label>

                              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
                                <div>
                                  <span className="text-sm font-medium">Can access POS</span>
                                  <p className="text-xs text-slate-500">Access to take orders and payments</p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={rolePerms.canAccessPos}
                                  onChange={(e) => updateRolePermissionsMutation.mutate({
                                    role: rolePerms.role,
                                    data: { canAccessPos: e.target.checked }
                                  })}
                                  className="w-5 h-5 accent-primary-600"
                                />
                              </label>
                            </div>

                            {/* Shift Constraints */}
                            <div className="space-y-3 pt-3 border-t border-slate-200">
                              <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                <Coffee className="w-4 h-4" />
                                Shift Constraints
                              </h4>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Max shift hours
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="24"
                                    value={rolePerms.maxShiftHours}
                                    onChange={(e) => updateRolePermissionsMutation.mutate({
                                      role: rolePerms.role,
                                      data: { maxShiftHours: parseInt(e.target.value) || 12 }
                                    })}
                                    className="input w-full"
                                  />
                                  <p className="text-xs text-slate-500 mt-1">
                                    Alert when exceeded
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Break reminder (minutes)
                                  </label>
                                  <input
                                    type="number"
                                    min="60"
                                    max="480"
                                    step="30"
                                    value={rolePerms.breakReminderMinutes}
                                    onChange={(e) => updateRolePermissionsMutation.mutate({
                                      role: rolePerms.role,
                                      data: { breakReminderMinutes: parseInt(e.target.value) || 240 }
                                    })}
                                    className="input w-full"
                                  />
                                  <p className="text-xs text-slate-500 mt-1">
                                    Show break reminder after this time
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="card p-6 space-y-6">
              <h2 className="text-lg font-semibold">Security Settings</h2>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
                  <div>
                    <span className="font-medium">Auto Clock-Out</span>
                    <p className="text-sm text-slate-500">
                      Automatically clock out employees who forget
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.autoClockOut}
                    onChange={(e) => updateSetting('autoClockOut', e.target.checked)}
                    className="w-5 h-5 accent-primary-600"
                  />
                </label>

                {settings.autoClockOut && (
                  <div className="pl-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Auto Clock-Out Time
                    </label>
                    <input
                      type="time"
                      value={settings.autoClockOutTime}
                      onChange={(e) => updateSetting('autoClockOutTime', e.target.value)}
                      className="input w-auto"
                    />
                  </div>
                )}

                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium mb-2">Two-Factor Authentication</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Add an extra layer of security to manager accounts
                  </p>
                  <div className="flex items-center gap-2 text-sm text-slate-600 bg-white p-3 rounded border">
                    <AlertCircle className="w-4 h-4 text-blue-500" />
                    <span>2FA will be available in a future update. Staff PIN codes provide POS-level security.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================
// RECEIVE PAYMENTS (STRIPE CONNECT)
// ===========================================
// The restaurant's own payout account: card payments at the POS route straight
// to their bank once onboarding is complete. Calm, three-state card:
// not connected -> setup in progress -> receiving payments.
function ConnectPayoutsCard() {
  const { data: statusData, refetch, isFetching } = useQuery({
    queryKey: ['stripe-connect-status'],
    queryFn: async () => {
      const response = await billingApi.connectStatus();
      return response.data?.data as { connected: boolean; onboarded: boolean; payoutsEnabled: boolean };
    },
  });

  const onboardMutation = useMutation({
    mutationFn: async () => {
      const response = await billingApi.connectOnboard();
      return response.data?.data as { url: string };
    },
    onSuccess: (data) => {
      // window.open after an await gets POPUP-BLOCKED (silently does
      // nothing — 'connect bank doesn't work'). Navigate instead: Stripe's
      // return_url brings the user back; on the native app Capacitor opens
      // external URLs in the system browser.
      if (data?.url) window.location.href = data.url;
    },
  });

  const status = statusData;
  const receiving = status?.connected && status?.payoutsEnabled;
  const inProgress = status?.connected && !status?.payoutsEnabled;

  return (
    <div className="p-4 bg-slate-50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium">Receive Payments</h3>
        {receiving && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
            <CheckCircle className="w-4 h-4" /> Active
          </span>
        )}
      </div>

      {receiving ? (
        <p className="text-sm text-slate-500">
          Card payments at the POS go directly to your bank account. Payouts
          typically arrive within 2 business days.
        </p>
      ) : inProgress ? (
        <>
          <p className="text-sm text-slate-500 mb-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
            Your payout setup is started but not finished — a few details are
            still needed before payments can reach your bank.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onboardMutation.mutate()}
              disabled={onboardMutation.isPending}
              className="btn btn-primary flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Finish Setup
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="btn btn-secondary"
            >
              {isFetching ? 'Checking…' : 'Check Status'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-4">
            Connect your bank account so card payments taken at the POS are
            deposited directly to you. Takes about 5 minutes — you'll need your
            business details and bank account number.
          </p>
          <button
            onClick={() => onboardMutation.mutate()}
            disabled={onboardMutation.isPending}
            className="btn btn-primary flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            {onboardMutation.isPending ? 'Opening…' : 'Connect Bank Account'}
          </button>
        </>
      )}
      {onboardMutation.isError && (
        <p className="text-sm text-red-600 mt-3">
          Couldn't open Stripe setup — please try again in a moment.
        </p>
      )}
    </div>
  );
}


// ===========================================
// PRINTERS (restaurant-wide)
// ===========================================
// Set once here; every POS device picks it up automatically. Test prints
// happen from a POS (the printer lives on the restaurant's local network).
function PrinterConfigCard() {
  const queryClient = useQueryClient();
  const { data: currentRestaurant } = useQuery({
    queryKey: ['restaurant-current-printers'],
    queryFn: async () => {
      const response = await restaurantApi.getCurrent();
      return response.data?.data;
    },
  });

  const saved = (currentRestaurant?.settings as any)?.printers || {};
  const [receiptIp, setReceiptIp] = useState('');
  const [kitchenIp, setKitchenIp] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (currentRestaurant && !loaded) {
      setReceiptIp(saved.receiptIp || '');
      setKitchenIp(saved.kitchenIp || '');
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant]);

  const saveMutation = useMutation({
    mutationFn: () =>
      restaurantApi.updateSettings({
        printers: {
          receiptIp: receiptIp.trim() || null,
          kitchenIp: kitchenIp.trim() || null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-current-printers'] });
    },
  });

  return (
    <div className="p-4 bg-slate-50 rounded-lg">
      <h3 className="font-medium mb-1">Printers</h3>
      <p className="text-sm text-slate-500 mb-4">
        Thermal printers on your restaurant's wifi (ESC/POS, port 9100). Every
        POS device uses these automatically — run a test print from any POS
        (printer icon in its header).
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Receipt printer IP</label>
          <input
            type="text"
            value={receiptIp}
            onChange={(e) => setReceiptIp(e.target.value)}
            placeholder="192.168.1.50"
            className="input w-full font-mono"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Kitchen printer IP <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={kitchenIp}
            onChange={(e) => setKitchenIp(e.target.value)}
            placeholder="192.168.1.51"
            className="input w-full font-mono"
          />
        </div>
      </div>
      <button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        className="btn btn-primary mt-4"
      >
        {saveMutation.isPending ? 'Saving…' : saveMutation.isSuccess ? 'Saved ✓' : 'Save Printers'}
      </button>
    </div>
  );
}

// ===========================================
// DAILY REPORT (automated morning email, per store)
// ===========================================
// Yesterday's sales, food cost, and expenses, emailed each morning to the
// people listed here. Saved to settings.dailyReport.recipients — the cron
// only sends where this list is non-empty, so an empty field means off.
function DailyReportCard() {
  const queryClient = useQueryClient();
  const { data: currentRestaurant } = useQuery({
    queryKey: ['restaurant-current-daily-report'],
    queryFn: async () => {
      const response = await restaurantApi.getCurrent();
      return response.data?.data;
    },
  });

  const saved = (currentRestaurant?.settings as any)?.dailyReport || {};
  const [recipients, setRecipients] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (currentRestaurant && !loaded) {
      setRecipients(Array.isArray(saved.recipients) ? saved.recipients.join(', ') : '');
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant]);

  const saveMutation = useMutation({
    mutationFn: () =>
      restaurantApi.updateSettings({
        dailyReport: {
          // keep the sent-marker and future fields intact
          ...saved,
          recipients: recipients
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.includes('@')),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurant-current-daily-report'] });
    },
  });

  return (
    <div className="p-4 bg-slate-50 rounded-lg">
      <h3 className="font-medium mb-1">Daily report</h3>
      <p className="text-sm text-slate-500 mb-4">
        Each morning, yesterday's sales, food cost, and expenses for this store —
        emailed to the people below. Leave the field empty to turn it off.
      </p>
      <label className="block text-sm font-medium text-slate-700 mb-1">Recipients</label>
      <input
        type="text"
        value={recipients}
        onChange={(e) => setRecipients(e.target.value)}
        placeholder="gm@store.com, chef@store.com"
        className="input w-full"
      />
      <p className="text-xs text-slate-400 mt-1">Separate addresses with commas.</p>
      <button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        className="btn btn-primary mt-4"
      >
        {saveMutation.isPending ? 'Saving…' : saveMutation.isSuccess ? 'Saved ✓' : 'Save Daily Report'}
      </button>
    </div>
  );
}
